import style from "@/styles/pages/MortgageTerm/MortgageTerm.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import { setApplicationProgress, setTermLength } from "@/slices/applicationSlice";
import SlideTransition from "@/styles/animation/SlideTransition";
import { createApiClient } from "@/lib/Requests";
import { ApplicationDto } from "@/lib/DTMWebAPIClient.generated";
import { setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function MortgageTerm() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            termLength: ""
        },
    });

    useEffect(() => {
        document.title = "Mortgage Term";
        methods.setValue("termLength", application ? application.termLength == 0 ? "" : application.termLength?.toString() : "");
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        client.apiApplicationsFetch().then((result) => {
            result.application = application;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });
    }, []);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const onSubmit = (data: any) => {
        dispatch(setTermLength(data.termLength != "unknown" ? +data.termLength : "unknown"));
        dispatch(setApplicationProgress(5));
        setClickInsightApplicationProgress('Your new mortgage - What mortgage term would you like?');
        navigate("/single/amortization");
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <div className="max-md:ml-2">
                            <h1 aria-label="Address Header">What mortgage term would you like?</h1>
                            <p className="sub-heading">This is how long you will commit to your interest rate, payment frequency, and other mortgage conditions.</p>
                        </div>
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div className="flex md:flex-row flex-col w-max">
                                        <div className="mt-5">
                                            <label htmlFor="mortgageTerm">
                                                Term
                                            </label>
                                            <div className="flex relative">
                                                <select
                                                    defaultValue=""
                                                    {...methods.register("termLength", {
                                                        required: "Please select an option",
                                                    })}
                                                    className={clsx(
                                                        "pt-2.5 mt-3"
                                                    )}
                                                    id="mortgageTerm"
                                                >
                                                    <option hidden value="">
                                                        Choose...
                                                    </option>
                                                    <option value="0.5">6 Months</option>
                                                    <option value="1">1 Year</option>
                                                    <option value="1.5">1.5 Years</option>
                                                    <option value="2">2 Years</option>
                                                    <option value="3">3 Years</option>
                                                    <option value="4">4 Years</option>
                                                    <option value="5">5 Years</option>
                                                    <option value="7">7 Years</option>
                                                    <option value="10">10 Years</option>
                                                    <option value="unknown">I don't know</option>
                                                </select>
                                            </div>
                                            {methods.formState?.errors?.termLength?.message && (
                                                <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                    {methods.formState?.errors?.termLength?.message.toString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                            >
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}